import { ColumnHeader, IColumnHeaderParams, Table } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import _ from 'lodash';
import { AudienceSourceType, DistributionType, IColumn, IDistributionContact } from '../../../models';
import { SurveyStatusType } from '../../../models/SurveyStatusType';
import { ColumnHelper } from '../../../utilities';
import { DistributionContactRow } from './DistributionContactRow';

export interface IDistributionContactListProps {
	surveyId: number;
	audienceId: number;
	distributionContacts: IDistributionContact[];
	distributionType?: DistributionType;
	columns: IColumn[];
	readonly?: boolean;
	sortParams: IColumnHeaderParams;
	searchText?: string;
	selectedDistributionContacts: IDistributionContact[];
	surveyStatus?: SurveyStatusType;
	audienceSourceType?: AudienceSourceType;
	onDelete: (surveyId: number, audienceId: number, distributionContactId: number) => any;
	handleSort: (field: string, sortAscDefault: boolean) => void;
	toggleCheckboxes?: (selectAllCheckboxChecked: boolean, DistributionContacts: IDistributionContact[]) => void;
}

export class DistributionContactsTable extends React.Component<
	IDistributionContactListProps,
	{
		selectAllCheckboxChecked: boolean;
	}
> {
	constructor(props: IDistributionContactListProps) {
		super(props);

		const { distributionContacts, selectedDistributionContacts } = this.props;
		this.state = {
			selectAllCheckboxChecked: this.isSelectAllCheckboxChecked(distributionContacts, selectedDistributionContacts),
		};

		this.getRow = this.getRow.bind(this);
		this.isSelectable = this.isSelectable.bind(this);
	}

	public UNSAFE_componentWillReceiveProps(nextProps: IDistributionContactListProps) {
		const { distributionContacts, selectedDistributionContacts } = nextProps;
		this.setState({ selectAllCheckboxChecked: this.isSelectAllCheckboxChecked(distributionContacts, selectedDistributionContacts) });
	}

	public render() {
		const { columns, distributionContacts, handleSort, sortParams } = this.props;
		const { selectAllCheckboxChecked } = this.state;
		const lastAlign = columns.find((c: IColumn) => c.name.toLowerCase() === 'actions') === undefined ? 'left' : 'right';

		const selectable = this.isSelectable();
		return (
			<Table responsive={true} lastalign={lastAlign} selectable={selectable ? 'true' : undefined}>
				<ColumnHeader
					columns={ColumnHelper.GetColumnHeaders(_.orderBy(this.getFilteredColumns(), ['displayOrder']))}
					handleSort={handleSort}
					sortAsc={sortParams.sortAsc}
					sortedBy={sortParams.sortedBy}
					selectable={selectable}
					headerCheckboxSortable={true}
					showHeaderCheckbox={selectable}
					headerCheckboxChecked={selectAllCheckboxChecked}
					onHeaderCheckboxChange={this.handleHeaderCheckboxChange}
				/>
				<tbody>{_.map(distributionContacts, this.getRow)}</tbody>
			</Table>
		);
	}

	public isSelectAllCheckboxChecked = (
		distributionContacts: IDistributionContact[],
		selectedDistributionContacts: IDistributionContact[],
	) => {
		return (
			_.difference(
				distributionContacts.map((dc: IDistributionContact) => {
					return dc.distributionContactId;
				}),
				selectedDistributionContacts.map((dc: IDistributionContact) => {
					return dc.distributionContactId;
				}),
			).length === 0
		);
	};

	public getFilteredColumns = () => {
		const { audienceSourceType, columns, distributionContacts, surveyStatus } = this.props;
		const excludeColumns: string[] = [];
		if (audienceSourceType === AudienceSourceType.Hris) {
			excludeColumns.push('actions');
		}
		if (surveyStatus !== SurveyStatusType.Scheduled) {
			excludeColumns.push('surveyCompletionStatus');
		}
		if (this.doesNotHaveAccessCodes(distributionContacts)) {
			excludeColumns.push('accessCode');
		}

		return columns.filter((column) => !excludeColumns.includes(column.name));
	};

	public getRow(distributionContact: IDistributionContact, index: any) {
		const { distributionType, searchText, selectedDistributionContacts, surveyStatus, onDelete } = this.props;

		const selectable = this.isSelectable();
		const isSelected =
			_.findIndex(selectedDistributionContacts, (contact) => {
				return contact.distributionContactId === distributionContact.distributionContactId;
			}) > -1;
		return (
			<DistributionContactRow
				key={index}
				surveyId={this.props.surveyId}
				audienceId={this.props.audienceId}
				distributionContact={distributionContact}
				distributionType={distributionType}
				columns={this.getFilteredColumns()}
				searchText={searchText}
				selectable={selectable}
				selected={isSelected}
				surveyStatus={surveyStatus}
				onDelete={onDelete}
				selectDistributionContact={(checked: boolean) => this.props.toggleCheckboxes!(checked, [distributionContact])}
			/>
		);
	}

	public handleHeaderCheckboxChange = () => {
		const { distributionContacts, toggleCheckboxes } = this.props;
		const { selectAllCheckboxChecked } = this.state;

		const checked = !selectAllCheckboxChecked;
		this.setState({ selectAllCheckboxChecked: checked }, () => {
			toggleCheckboxes!(checked, distributionContacts);
		});
	};

	public isSelectable() {
		const { readonly, surveyStatus, audienceSourceType } = this.props;
		return (
			!readonly &&
			audienceSourceType !== AudienceSourceType.Hris &&
			(surveyStatus === SurveyStatusType.Scheduled || surveyStatus === SurveyStatusType.SurveyInProgress)
		);
	}

	public doesNotHaveAccessCodes(contacts: IDistributionContact[]) {
		return contacts?.filter((c) => c.accessCode !== null || c.accessCode !== undefined).length == 0;
	}
}
